import { useContext } from 'react';

import { WWContext, type WWContextValue } from '../components/WWContext';
import { useRsvpIsVisible } from './useRsvpIsVisible';
import { LITE_SITE_TYPE, useIsLiteSite } from './useIsLiteSite';

import { isWwsWeddingWebsiteV2, type Wws_Page } from '../types/graphql';

export const WEDDING_PAGE_TYPE = 'WeddingPage';

const CONTENT_PAGE = 'ContentPage';
const GUEST_BOOK_PAGE = 'GuestBookPage';
const REGISTRY_PAGE = 'RegistryPage';

interface HomePageOverride {
  pageName?: string;
  routeName?: string;
}

interface RSVPPageOverride {
  routeName?: string;
}

export interface NavLinkOverrides {
  homePageOverride?: HomePageOverride;
  rsvpPageOverride?: RSVPPageOverride;
  registryPageOverride?: RSVPPageOverride;
}

export function useNavLink(navLinkOverrides: NavLinkOverrides | undefined = undefined): Partial<Wws_Page>[] | undefined {
  const { data } = useContext<WWContextValue>(WWContext);

  const rsvpNavItem = useRsvpIsVisible();

  const isLiteSite = useIsLiteSite();

  const pages = isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)
    ? data?.weddingWebsiteResult.pages
      ?.filter((page: Wws_Page): boolean => {
        if (!page.title) {
          return false;
        }

        // Guest Book && Mysterious details page
        if (
          (page.type === CONTENT_PAGE && page.routeName === 'details') ||
          (page.type === GUEST_BOOK_PAGE && page.routeName === 'guest-book')
        ) {
          return false;
        }

        // Return true on the RegistryPage if it is shown, since it will always be empty
        if (page.type === REGISTRY_PAGE && page.show) {
          return true;
        }

        if (
          isLiteSite &&
          page.items.length < 1 &&
          page.type !== LITE_SITE_TYPE
        ) {
          // If this page has no items, do not return it
          return false;
        }

        if (isLiteSite && page.type === WEDDING_PAGE_TYPE) {
          // Don't return a WeddingPage on LiteSites.
          // Instead the LiteSitePage will be used for the Home page.
          return false;
        }

        return page.show || page.type === WEDDING_PAGE_TYPE;
      })
      .map((page: Wws_Page): Wws_Page => {
        if (page.type === WEDDING_PAGE_TYPE) {
          return {
            ...page,
            title: navLinkOverrides?.homePageOverride?.pageName || 'Home',
            routeName: navLinkOverrides?.homePageOverride?.routeName || page.routeName,
          } as Wws_Page;
        } else if (page.type === REGISTRY_PAGE) {
          return {
            ...page,
            routeName: navLinkOverrides?.registryPageOverride?.routeName || page.routeName,
          } as Wws_Page;
        }

        return page;
      })
    : undefined;

  if (typeof pages !== 'undefined' && rsvpNavItem) {
    // To keep consistency we create a fake rsvp page item only used for nav. This can be removed after
    // rsvpAsPage experiment is called
    return [
      ...pages,
      {
        id: 0,
        title: 'RSVP',
        routeName: navLinkOverrides?.rsvpPageOverride?.routeName || 'rsvp',
        items: [],
        type: 'RsvpNavItem',
        show: true,
      },
    ];
  }

  return pages;
}

export interface UseLiteSiteNav {
  pages: Partial<Wws_Page>[] | undefined;
  showNav: boolean;
}

export function useLiteSiteNav(): UseLiteSiteNav {
  const pages: Partial<Wws_Page>[] | undefined = useNavLink();

  let showNav = false;

  pages?.forEach((page: Partial<Wws_Page>): void => {
    if (page.type !== LITE_SITE_TYPE && page.type !== 'RsvpNavItem') {
      showNav = true;
    }
  });

  return { pages, showNav };
}
